import { getBreadcrumbItems } from "configApp";
import { useCallback, useMemo } from "react";
import { saveAs } from "file-saver"
import { useNavigate } from "react-router";


import { useAppDispatch, useAppSelector } from "store/hooks";
import * as ProjectStore from "store/projectSlice";
import * as heatModeStore from "store/heatModeSlice";
import * as coolModeStore from "store/coolModeSlice";
import { getAppUrl } from "utils/defines";
import { useSalesNetwork } from "./useSalesNetworks";
import _ from 'lodash';
import useTranslation from "./useTranslation";
import { addNotification } from "store/componentsSlice";
import { useCreateClientMutation, useCreateProjectMutation, useCreateProjectTraceMutation, useGeneratePdfMutation, useGenerateXlsxMutation, useUpdateClientMutation, useUpdateProjectMutation, useUpdateProjectTraceMutation } from "store/api";
import { scrollToWindowTop, toJS } from "utils/helpers";
import { getRadiatorClassI18nCode, getTypeEmetteurI18nCode } from "constants/dropdown";
import { getProjectSchemaCode } from "utils/helpers/projectSchema";

export const useProject = () => {
    const projectState = useAppSelector((state) => state.project);
    const authState = useAppSelector((state) => state.auth);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { network } = useSalesNetwork();
    const [generateXlsxAction] = useGenerateXlsxMutation();
    const [generatePdfAction] = useGeneratePdfMutation()
    const [updateClient] = useUpdateClientMutation();
    const [createClient] = useCreateClientMutation();
    const [updateProject] = useUpdateProjectMutation();
    const [createProject] = useCreateProjectMutation();
    const [createProjectTrace] = useCreateProjectTraceMutation();
    const [updateProjectTrace] = useUpdateProjectTraceMutation();

    const initNewProject = useCallback(() => {
        dispatch(ProjectStore.initNewProject());
        dispatch(heatModeStore.initNewProject());
        dispatch(coolModeStore.initNewProject());
        //hpChoiceStore.initNewProject();
        //saveProjectStore.initNewProject();
    }, [dispatch])

    const clientService_insertOrUpdate = useCallback(async (client: any) => {
        const payload: any = { ...client, user_id: authState.profile.id };
        if (payload.id) {
            //return await apiService.patch(`client/${client.id}`, client);
            return await updateClient(payload).unwrap();
        } else {
            //return await apiService.post('client', client);
            return await createClient(payload).unwrap();
        }
    }, [authState.profile.id, createClient, updateClient])

    const projectTracePrepare = useCallback(async () => { //NOSONAR
        const data = projectState.project.header;// formStore.form;
        try {
            if (data?.registerClient) {
                await clientService_insertOrUpdate(data.client);
            }

            if (projectState.project.results) {
                //
            }

            const _project = {
                project_id: projectState.project.id,
                user_id: authState.profile.id,
                environnement_id: network?.id,
                data: { ..._.cloneDeep(toJS(projectState.project)), ...{ header: data } },
                version_index: true,
            };

            // Cleanup trace
            delete _project.data.forms.heatModeStep.form.departmentsList;
            delete _project.data.forms.heatModeStep.form.stationsList;

            if (_project.data.results?.E1?.length > 0) {
                _project.data.results.E1.forEach((p: any) => {
                    if (p.E2) delete p.E2;
                    if (p.E3) delete p.E3;
                    if (p.E4) delete p.E4;
                    if (p.E5) delete p.E5;
                })
            }

            if (_project.data.selectedProduct) {
                if (_project.data.selectedProduct.E2) delete _project.data.selectedProduct.E2;
                if (_project.data.selectedProduct.E3) delete _project.data.selectedProduct.E3;
            }

            if (_project.data.accessories) {
                if (_project.data.accessories.accessoriesGroups) delete _project.data.accessories.accessoriesGroups;
                if (_project.data.accessories.otherAccessories && _project.data.accessories.otherAccessories.length > 0) {
                    _project.data.accessories.otherAccessories = _project.data.accessories.otherAccessories.filter((a: any) => a._selected);
                }
                if (_project.data.accessories.recommendedAccessories && _project.data.accessories.recommendedAccessories.length > 0) {
                    _project.data.accessories.recommendedAccessories = _project.data.accessories.recommendedAccessories.filter((a: any) => a._selected);
                }
            }

            return (_project);
        } catch (error) {
            console.error(error);
            return Promise.reject(error);
        }
    }, [projectState.project, clientService_insertOrUpdate, authState.profile.id, network])

    const projectTraceCreate = useCallback(async () => {
        const project = await projectTracePrepare();
        const data = await createProjectTrace({ env: network?.id, project: project }).unwrap();
        dispatch(ProjectStore.setTraceId(data.id));
    }, [dispatch, createProjectTrace, projectTracePrepare, network])

    const projectTraceUpdate = useCallback(async () => {
        const project = await projectTracePrepare();
        await updateProjectTrace({ traceId: projectState.traceId, project: project }).unwrap();
    }, [projectState, updateProjectTrace, projectTracePrepare])

    const changeStep = useCallback((step: number) => {
        dispatch(ProjectStore.changeStep(step));

        if (step > 3 && projectState.traceId) {
            projectTraceUpdate();
        }
    }, [dispatch, projectState.traceId, projectTraceUpdate])

    const nextStep = useCallback(() => {
        // if (projectState.project.currentStep === 0 && location.pathname.indexOf('/home/heat') > 0 && projectState.project.simulationCool) {
        //     //history.push(`/${UISTore.env}/project/home/cool`);
        //     navigate(getAppUrl('/project/home/cool'))
        //     window.scrollTo(0, 0);
        //     return;
        // }

        const _nextStep = Math.min(projectState.maxStep, projectState.project.currentStep + 1);
        if (_nextStep > projectState.project.currentStep) {
            const found = getBreadcrumbItems(network?._parameters.disabledSteps)[_nextStep];
            if (found) {
                navigate(getAppUrl(`/project/` + found.id + (found.params ? `/${found.params}` : '')));
                window.scrollTo(0, 0);
            }
        }
    }, [projectState.project.currentStep, projectState.maxStep, navigate, network?._parameters.disabledSteps])

    const previousStep = useCallback(() => {
        const _previousStep = Math.max(projectState.minStep, projectState.project.currentStep - 1);
        if (_previousStep < projectState.project.currentStep) {
            const found = getBreadcrumbItems(network?._parameters.disabledSteps)[_previousStep];
            if (found) {
                changeStep(_previousStep);
                //history.push(`/${UISTore.env}/project/` + found.id + (found.params ? `/${found.params}` : ''));
                navigate(getAppUrl(`/project/` + found.id + (found.params ? `/${found.params}` : '')));
                window.scrollTo(0, 0);
            }
        }
    }, [projectState, navigate, network?._parameters.disabledSteps, changeStep])

    const goToStep = useCallback((index: number) => {
        const items = getBreadcrumbItems(network?._parameters.disabledSteps);
        let found = items[index];
        if (!found && index > items.length - 1) {
            found = items[items.length - 1];
        }
        if (!found) {
            found = getBreadcrumbItems(network?._parameters.disabledSteps)[0];
        }

        changeStep(found.index);
        //history.push(`/${UISTore.env}/project/` + found.id + (found.params ? `/${found.params}` : ''));
        navigate(getAppUrl(`/project/` + found.id + (found.params ? `/${found.params}` : '')));
        //scrollToWindowTop();

    }, [network, navigate, changeStep])

    const computeSchemaCode = useCallback(() => {
        const schemaCode = getProjectSchemaCode(
            projectState.project.forms.hpChoiceStep.form ?? {},
            projectState.project.forms.heatModeStep.form?.zones,
        );

        dispatch(ProjectStore.setSchemaCode(schemaCode));

        // dispatch is async, so return the freshly computed value for immediate use
        return schemaCode;

    }, [dispatch, projectState.project.forms.heatModeStep.form?.zones,
        projectState.project.forms.hpChoiceStep.form?.releveChaudiere,
        projectState.project.forms.hpChoiceStep.form?.typeECS])


    const quoteGetLineTotal = (item: any) => {
        const discount = (100 - parseFloat(item._discount)) / 100;
        //const _t = (parseFloat(item.price) + parseFloat(item.eco)) * (parseFloat(item._qty)) * (isNaN(discount) ? 0 : discount)
        const _t = (parseFloat(item.price) * (isNaN(discount) ? 1 : discount) + parseFloat(item.eco)) * (parseFloat(item._qty));
        //console.log('quoteGetLineTotal: ' + _t)
        if (isNaN(_t)) {
            return 0;
        }
        return _t;
    }
    const quoteGetTotal = (total: number, applyVAT: boolean, vat: number): any => {
        const _t = isNaN(total) ? 0 : total * (applyVAT && vat ? 1 + vat / 100 : 1); //NOSONAR
        //console.log('quoteGetTotal/total/vat: ' + _t + '/' + total + '/' + vat)
        return _t;
    }

    const computeTotal = useCallback((lines: any, vat: number, isOtherLine: boolean = false, changeLine: boolean = false): number => {
        if (!lines || lines.length <= 0) return 0;

        let total = 0;

        const _lines: any = [];
        lines.forEach((line: any) => {
            //
            if (!line._selected) return;
            const _line = { ...line };
            _line._i18ntype = t(_line._type.toUpperCase());
            _line._total = quoteGetLineTotal(_line);

            total += _line._total;

            _line._total = _line._total.round(2);

            _lines.push(_line);
        });

        if (changeLine) {
            if (isOtherLine) {
                dispatch(ProjectStore.setQuoteLines(_lines));
            } else {
                dispatch(ProjectStore.setLines(_lines));
            }
        }

        return quoteGetTotal(total, true, vat).round(2);
    }, [t])


    // Quote
    const _generateXlsx = useCallback(async (lines: any, vat: number) => {
        const data = {
            header: {
                i18n_cat: t("QUOTE_XLSX_CATEGORY"),
                i18n_code: t("QUOTE_XLSX_CODE"),
                i18n_desc: t("QUOTE_XLSX_DESC"),
                i18n_qty: t("QUOTE_XLSX_QTY"),
                i18n_price: t("QUOTE_XLSX_PRICE", { currency: network?._parameters.currency }),
                i18n_discount: t("QUOTE_XLSX_DISCOUNT"),
                i18n_eco: t("QUOTE_XLSX_ECO", { currency: network?._parameters.currency }),
                i18n_total: t("QUOTE_XLSX_TOTAL", { currency: network?._parameters.currency })
            },
            lines: lines,
            i18n_vtaHeader: t("QUOTE_XLSX_VTA"),
            vta: vat,
            i18n_totalTTCHeader: t("QUOTE_XLSX_TOTALTTC", { currency: network?._parameters.currency }),
            total: 0
        };

        data.total = computeTotal(data.lines, vat);
        return generateXlsxAction({ data: data });
        //return generateXlsxAction({ data: data });//return await apiService.post('generateXlsx', data, { responseType: 'blob' });
    }, [computeTotal, generateXlsxAction, network?._parameters.currency, t])

    const isHybride = useCallback(() => {
        return projectState.project?.forms?.hpChoiceStep?.form?.heatPumpType === '3'
    }, [projectState.project?.forms?.hpChoiceStep?.form?.heatPumpType])

    const alllQuoteLines = useCallback(() => {
        return projectState.project.quote.lines.slice().concat(projectState.project.quote.otherlines.slice());
    }, [projectState.project.quote.lines, projectState.project.quote.otherlines])



    const generateXlsx = useCallback((selectedVAT: number) => {
        _generateXlsx(alllQuoteLines(), selectedVAT)
            .then((res: any) => {
                saveAs(res.data, `${network?._parameters.brand}-quote.xlsx`)
            }).catch(() => {
                dispatch(addNotification({
                    type: "error",
                    message: t('EXPORT_ERROR')
                }))
            })
    }, [_generateXlsx, alllQuoteLines, dispatch, network?._parameters.brand, t])

    async function _generateCsv(lines: any[]) {
        const csv = [["Numero", "Type", "Option", "Parent", "CodeArticle", "Description", "Quantite", "PrixPublic"]];

        lines.forEach((line, index) => {
            csv.push([
                index + 1, "P",
                "", "",
                line.code_env,
                "",
                line._qty,
                "",
            ]);
        });

        const csvContent = csv.map(e => e.join(";")).join("\n");
        return { data: new Blob([csvContent], { type: "text/plain;charset=utf-8" }) };
    }

    const generateCsv = useCallback(() => {
        _generateCsv(projectState.project.quote.lines).then((res: any) => {
            saveAs(res.data, `${network?._parameters.brand}-export.csv`)
        }).catch(() => {
            dispatch(addNotification({
                type: "error",
                message: t('EXPORT_ERROR')
            }))
        })

    }, [dispatch, network?._parameters.brand, projectState.project.quote.lines, t])

    const generateSynthesis = useCallback((pages: any, header: any) => {
        const newProject = _.cloneDeep({ ...projectState.project, ...header });
        const zones = newProject?.forms?.heatModeStep?.form?.zones;

        //For old etudes 
        if (Array.isArray(zones)) {
            zones.forEach((zone: any) => {
                if (!Array.isArray(zone?.detail)) return;
                zone.detail.forEach((item: any) => {
                    if (!item) return;
                    if (!item.radiatorClass_i18nCode) {
                        item.radiatorClass_i18nCode = getRadiatorClassI18nCode(item.radiatorClass);
                    }
                    if (!item.radiatorModel_i18nCode) {
                        item.radiatorModel_i18nCode = getTypeEmetteurI18nCode(item.radiatorClass, item.radiatorModel);
                    }
                });
            });
        }

        return generatePdfAction({ data: { project: newProject, pages: pages, env: network?.code, traceId: projectState.traceId } })
    }, [generatePdfAction, network?.code, projectState.traceId, projectState.project])



    const insertOrUpdate = useCallback(async (project: any) => {
        try {
            if (project.id) {
                //return await apiService.patch(`project/${project.id}`, project);
                return await updateProject(project).unwrap()
            } else {
                //return await apiService.post('project', project);
                return await createProject(project).unwrap()
            }

        } catch (error) {
            console.error(error);
            return Promise.reject(error);

        }

    }, [createProject, updateProject])


    const save = useCallback(async (data: any) => {
        try {
            if (data.registerClient) {
                await clientService_insertOrUpdate(data.client);
            }
            const traceId = localStorage.getItem("traceId");

            const isSameUser = projectState.project.user_id === authState.profile.id;

            let project = {
                id: projectState.project.id,
                user_id: authState.profile.id,
                environnement_id: network?.id,
                ...(+(traceId || 0) && { traceId: +(traceId || 0) }),
                data: { ...projectState.project, ...{ header: data } }
            };

            try {
                // ToDo: check recommendedAccessories to fix this issue
                if (!project.data.accessories.recommendedAccessories) {
                    project = { ...project, data: { ...project.data, accessories: { ...project.data.accessories, recommendedAccessories: [] } } };
                }
            } catch {

            }

            if (!project.id || !isSameUser) delete project.id;


            //throw new Error("disable save");

            return await insertOrUpdate(project);
            //return Promise.resolve(project)

        } catch (error) {
            console.error(error);
            return Promise.reject(error);

        }
    }, [authState.profile.id, clientService_insertOrUpdate, insertOrUpdate, network?.id, projectState.project])

    const updateProjectId = useCallback((projectId: any) => {
        dispatch(ProjectStore.updateProjectId(projectId))
    }, [dispatch])

    const setHeader = useCallback((header: any) => {
        dispatch(ProjectStore.setHeader(header))
    }, [dispatch])

    const saveStore = useCallback(() => {
        dispatch(ProjectStore.saveStore())
    }, [dispatch])

    const setAccessories = useCallback((acc: any) => {
        dispatch(ProjectStore.setAccessories(acc))
    }, [dispatch])

    const setVat = useCallback((vat: any) => {
        dispatch(ProjectStore.setVat(vat));
    }, [dispatch])

    const setComment = useCallback((comment: string) => {
        dispatch(ProjectStore.setComment(comment));
    }, [dispatch])

    const setQuoteTotal = useCallback((total: number) => {
        dispatch(ProjectStore.setQuoteTotal(total));
    }, [dispatch])

    const setQuoteTotalHT = useCallback((total: number) => {
        dispatch(ProjectStore.setQuoteTotalHT(total));
    }, [dispatch])

    const setQuoteLines = useCallback((data: any) => {
        dispatch(ProjectStore.setQuoteLines(data));
    }, [dispatch])

    const updateForm = useCallback((form: string, data: any) => {
        dispatch(ProjectStore.updateForm({ form, data } as any));
    }, [dispatch])

    const resetResults = useCallback(() => {
        dispatch(ProjectStore.resetResults());
    }, [dispatch])

    const setCoolMode = useCallback((data: any) => {
        dispatch(ProjectStore.setCoolMode(data));
    }, [dispatch])

    /**
    * Return translated value or value of a dropdown list selectd item
    * @param path
    * @returns {*}
    */
    const getValue = useCallback((path: string) => {
        let value = _.get(projectState.project, path + '_i18nCode');
        if (value != null) {
            return t(value);
        }

        value = _.get(projectState.project, path + '_value');
        if (value != null) {
            return value;
        }

        // Return code if no value found
        return _.get(projectState.project, path) || 0;
    }, [projectState.project, t])

    return useMemo(
        () => ({
            initNewProject,
            isHybride,
            changeStep,
            nextStep,
            previousStep,
            goToStep,
            getValue,
            computeSchemaCode,
            quoteGetLineTotal,
            quoteGetTotal,
            generateCsv,
            generateXlsx,
            generateSynthesis,
            save,

            projectTraceCreate,
            projectTraceUpdate,

            updateProjectId,
            setHeader,
            saveStore,

            updateForm,
            resetResults,

            setAccessories,
            projectTracePrepare,
            setVat,
            setComment,
            setQuoteTotal,
            setQuoteTotalHT,
            setQuoteLines,
            computeTotal,
            setCoolMode,

            projectState
        }),
        [
            computeSchemaCode, generateCsv, generateSynthesis, generateXlsx,
            getValue, goToStep, initNewProject, isHybride, nextStep, previousStep, projectTraceCreate,
            projectTraceUpdate, save, saveStore, setHeader, updateProjectId, changeStep, projectTracePrepare,
            setVat, setAccessories, computeTotal, setComment, setQuoteTotal, setQuoteTotalHT, setQuoteLines,
            updateForm, resetResults,

            projectState,
        ]
    );
}